import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { ExpirationValue } from './SeatExpirationCard';
import { postUpdateSubscriptionSeatRequest } from '@client/model/subscriptionSeatsModel';
import { selectSubscriptionSeatsState } from '@client/model/selectors';
import { getExpirationDate } from './expirationUtils';

interface UseEditSeatExpirationProps {
  onSave?: (seatId: string, expirationDate: string | null) => void;
  initialExpiresDateTime?: Date | null;
}

export function useEditSeatExpiration({
  onSave,
  initialExpiresDateTime,
}: UseEditSeatExpirationProps = {}) {
  const dispatch = useDispatch();
  const subscriptionSeatsState = useSelector(selectSubscriptionSeatsState);
  const targetAdvisorId = subscriptionSeatsState.targetAdvisorId;

  // Calculate initial state from initialExpiresDateTime
  const getInitialExpirationValue = (): ExpirationValue => {
    if (!initialExpiresDateTime) {
      return {
        mode: 'no_end_date',
        monthsFromToday: 0,
        specificDate: '',
      };
    }

    // Validate that initialExpiresDateTime is a Date object
    if (!(initialExpiresDateTime instanceof Date) || isNaN(initialExpiresDateTime.getTime())) {
      console.error('Invalid date provided to useEditSeatExpiration');
      return {
        mode: 'no_end_date',
        monthsFromToday: 0,
        specificDate: '',
      };
    }

    // Format Date to ISO date string (YYYY-MM-DD) in local time
    const dateStr = format(initialExpiresDateTime, 'yyyy-MM-dd');

    return {
      mode: 'specific_date',
      monthsFromToday: 0,
      specificDate: dateStr,
    };
  };

  const [expirationValue, setExpirationValue] = useState<ExpirationValue>(
    getInitialExpirationValue(),
  );

  const handleSave = (seatId: string) => {
    if (!targetAdvisorId) {
      console.error('Cannot update seat: session advisor not found');
      return;
    }

    const expirationDate = getExpirationDate(expirationValue);
    dispatch(
      postUpdateSubscriptionSeatRequest({
        advisorId: targetAdvisorId,
        subscriptionSeatId: seatId,
        expiresDateTime: expirationDate,
      }),
    );
    if (onSave) {
      onSave(seatId, expirationDate);
    }
  };

  return {
    expirationValue,
    setExpirationValue,
    handleSave,
    getExpirationDate: () => getExpirationDate(expirationValue),
  };
}
